import React, { useContext, useEffect, useState } from 'react'

import { Spinner, ToggleSwitch } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { compose } from 'recompose'

import { USER_UUID } from 'app-constants'
import AgreementDoc from 'components/AgreementDoc/AgreementDoc'
import AgreementModal from 'components/AgreementModal'
import CenteredSpinner from 'components/CenteredSpinner'
import { CTAContainer } from 'components/CTAContainer'
import { isMobile } from 'components/Trackers/platform'
import {
  AcceptanceDocument,
  ActionType,
  CohortEligibilityScope,
  PageName,
  ProductKey,
  SpendAccount,
  SubscribeToTierInput,
  SubscribeToTierResult,
  TierOption,
} from 'generated/types'
import { hasOrderedCard } from 'pages/CardOrderReview/useCardStatusPolling'
import { useTierGroupForUser } from 'pages/PricingInterstitial/useTierGroupForUser'
import { TestIDs } from 'test/utils/testIds'
import trackEvent from 'utils/analytics/trackEvent'
import { sendToSentry } from 'utils/errors'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getFlow } from 'utils/FlowProvider/utils'
import { FunnelContext } from 'utils/FunnelContext'
import { useViewportMeta } from 'utils/hooks/useViewportMeta'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { mocDDWaiver, passions } from 'utils/Optimizely/features'
import { setTierName, setTierPrice, setTierPriceIdCookie } from 'utils/TieredRegistration/utils'

import CohortEligibilityQuery from '../gql/CohortEligibility.query.gql'
import SubscribeToTierMutation from '../gql/SubscribeToTier.mutation.gql'
import GrayVector from './assets/gray-vector.svg'
import GreenVector from './assets/green-vector.svg'
import CardImage from './assets/moc-card-corner.svg'
import BenefitsSection from './benefitsSection'
import {
  DisclosureButtonTappedEvent,
  NextButtonTappedEvent,
  ScreenViewedEvent,
  ScrollBottomEvent,
} from './events'
import agreementDocQuery from './gql/agreementDoc.query.gql'
import cardOrderStatusQuery from './gql/cardOrderStatus.query.gql'
import {
  ActionWrapperDivider,
  DirectDepositText,
  Divider,
  HeaderRow,
  ItemDivider,
  MOCLayout,
  PrivacyPolicy,
  StyledButton,
  StyledContainer,
  StyledHeading,
  StyledImage,
  SwitchContainer,
  TierDescriptionText,
  TierPriceContainer,
  TierPriceText,
  TierPriceVector,
  TopActionWrapper,
  WaivedTierPriceText,
  WaivedTierPriceVector,
} from './styled-components'

type Props = WithFlowRouterProps & WithOptimizelyProps

interface QueryResponse {
  acceptanceDocument: AcceptanceDocument
}

type CohortEligibilityResponse = {
  cohortEligibility: {
    eligible: boolean
  }
}

type SubscribeToTierInputs = {
  input: SubscribeToTierInput
}

type SubscribeToTierResults = {
  subscribeToTier: SubscribeToTierResult
}

const enhance = compose<Props, {}>(withFlowRouter, withOptimizely)

const MOCTierPricing: React.FunctionComponent<Props> = ({ route, isFeatureEnabled }) => {
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const { getRemoteString } = useRemoteStrings()
  const [isShowingTerms, setIsShowingTerms] = useState<boolean>(false)
  const [toggleChecked, setToggleChecked] = useState<boolean>(false)
  const { isScrolledToBottom, setIsLoading } = useViewportMeta()

  const isPassionsEnabled = isFeatureEnabled(passions)
  const mocDDWaiverEnabled = isFeatureEnabled(mocDDWaiver)

  const { data: queryData, loading: isQueryLoading } = useQuery<QueryResponse>(agreementDocQuery, {
    fetchPolicy: 'network-only',
  })
  const { data: spendAccountData, loading: isSpendAccountLoading } = useQuery<{
    spendAccount: SpendAccount
  }>(cardOrderStatusQuery, {
    fetchPolicy: 'network-only',
  })

  const { data: cohortEligibilityData, loading: isCohortEligibilityLoading } = useQuery<
    CohortEligibilityResponse
  >(CohortEligibilityQuery, {
    fetchPolicy: 'network-only',
    skip: !isPassionsEnabled,
    variables: { cohortScope: [CohortEligibilityScope.PassionsExperience] },
  })

  const { data: tierGroupForUserData, loading: isTierGroupForUserLoading } = useTierGroupForUser()

  const [subscribeToTierMutation, { loading: isSubscribeToTierLoading }] = useMutation<
    SubscribeToTierResults,
    SubscribeToTierInputs
  >(SubscribeToTierMutation)

  useEffect(() => {
    trackEvent('Screen Viewed', {
      eventData: ScreenViewedEvent,
      flow: getFlow(),
    })
    setIsLoading(false)
  }, [setIsLoading])

  useEffect(() => {
    if (isScrolledToBottom) {
      trackEvent('Screen Viewed', {
        eventData: ScrollBottomEvent,
        flow: getFlow(),
      })
    }
  }, [isScrolledToBottom])

  const isPassionsEligible =
    !!cohortEligibilityData?.cohortEligibility[0]?.eligible && isPassionsEnabled

  const mocTiers: TierOption[] = tierGroupForUserData?.tierGroupForUser?.tierOptions
    ?.filter(tierOption =>
      tierOption?.tier?.products?.find(product => product.key === ProductKey.MightyOakCard),
    )
    .sort(
      (firstTier, lastTier) =>
        lastTier.tier.tierPrice.amount.value - firstTier.tier.tierPrice.amount.value,
    )

  const hasMultipleTiers = mocTiers?.length > 1

  const userId = window.sessionStorage.getItem(USER_UUID)

  const optionLabels = mocTiers?.map(({ tier }) => tier.name)

  let selectedTier: TierOption

  if (mocTiers) {
    hasMultipleTiers
      ? (selectedTier = toggleChecked ? mocTiers[1] : mocTiers[0])
      : (selectedTier = mocTiers[0])
  }

  const selectedTierProducts = selectedTier?.tier.products.map(({ key }) => key)
  const highestTierProducts = mocTiers?.[0].tier.products.map(({ key }) => key)

  const handleSwitchChange = () => {
    setToggleChecked(!toggleChecked)
  }
  const handleNext = async () => {
    setTierName(selectedTier?.tier?.name)
    setTierPriceIdCookie(selectedTier?.tier?.tierPrice?.id)

    setTierPrice(selectedTier?.tier?.tierPrice?.amount?.value)

    trackEvent('Button Tapped', {
      eventData: NextButtonTappedEvent(
        getRemoteString({ key: 'registration.moc_tier.cta.confirm' }),
      ),
      flow: getFlow(),
    })

    try {
      const subscribeToTierMutationResponse = await subscribeToTierMutation({
        variables: {
          input: {
            tierPriceId: selectedTier?.tier?.tierPrice?.id,
            tierId: selectedTier?.tier?.id,
            userId,
          },
        },
      })

      const typenameResponse = subscribeToTierMutationResponse?.data?.subscribeToTier?.__typename
      if (typenameResponse !== 'TierSubscription') {
        throw new Error(`Subscribe user to tier failed with response ${typenameResponse}`)
      }

      if (hasOrderedCard(spendAccountData.spendAccount.bankCardOrder.cardOrderStatus)) {
        const state = { hasOrderedCard: true }
        isDynamicFunnelEnabled
          ? routeToPage(PageName.PricingInterstitial, ActionType.Skip, state)
          : route({ from: FlowRoute.PricingInterstitial, action: 'skip', locationState: state })
      } else {
        isDynamicFunnelEnabled
          ? routeToPage(PageName.PricingInterstitial, ActionType.Continue)
          : route({ from: FlowRoute.PricingInterstitial, action: 'continue' })
      }
    } catch (error) {
      sendToSentry(error, {
        message: `Unable to subscribe user to ${selectedTier?.tier?.key} tier.`,
      })
    }
  }

  const handleOpenModal = () => {
    trackEvent('Button Tapped', {
      eventData: DisclosureButtonTappedEvent,
      flow: getFlow(),
    })
    setIsShowingTerms(true)
  }

  const handleCloseModal = () => {
    setIsShowingTerms(false)
  }

  if (
    isQueryLoading ||
    isTierGroupForUserLoading ||
    isCohortEligibilityLoading ||
    isSpendAccountLoading ||
    selectedTier === undefined
  ) {
    return <CenteredSpinner />
  }
  return (
    <>
      {hasMultipleTiers && isMobile() && (
        <TopActionWrapper>
          <SwitchContainer>
            <ToggleSwitch
              data-testid={TestIDs.PRICING_INTERSTITIAL.MOC_TIER_SWITCH}
              checked={toggleChecked}
              onChange={handleSwitchChange}
              optionLabels={optionLabels}
            />
          </SwitchContainer>
          <ItemDivider />
        </TopActionWrapper>
      )}
      <MOCLayout>
        {hasMultipleTiers && !isMobile() && (
          <SwitchContainer>
            <ToggleSwitch
              data-testid={TestIDs.PRICING_INTERSTITIAL.MOC_TIER_SWITCH}
              checked={toggleChecked}
              onChange={handleSwitchChange}
              optionLabels={optionLabels}
            />
          </SwitchContainer>
        )}
        <HeaderRow>
          <StyledContainer>
            <StyledHeading data-testid={TestIDs.PRICING_INTERSTITIAL.MOC_TITLE}>
              {selectedTier?.tier?.name}
            </StyledHeading>
            {mocDDWaiverEnabled && (
              <>
                <TierPriceContainer>
                  <TierPriceVector src={GrayVector} />
                  <TierPriceText data-testid={TestIDs.PRICING_INTERSTITIAL.MOC_TIER_PRICE}>
                    {`$${selectedTier?.tier?.tierPrice?.amount?.value?.toString()}`}
                  </TierPriceText>
                  <WaivedTierPriceVector src={GreenVector} />
                  <WaivedTierPriceText
                    data-testid={TestIDs.PRICING_INTERSTITIAL.MOC_WAIVED_TIER_PRICE}
                  >
                    {getRemoteString({ key: 'registration.moc_tier.header.subtitle' })}
                  </WaivedTierPriceText>
                </TierPriceContainer>

                <DirectDepositText data-testid={TestIDs.PRICING_INTERSTITIAL.MOC_TIER_DISCLOSURE}>
                  {getRemoteString({
                    key: 'registration.moc_tier.header.disclosure_variable',
                    variables: [`$${selectedTier?.tier?.tierPrice?.amount?.value?.toString()}`],
                  })}
                </DirectDepositText>
              </>
            )}
            <TierDescriptionText data-testid={TestIDs.PRICING_INTERSTITIAL.MOC_HEADER_BODY}>
              {getRemoteString({ key: 'registration.moc_tier.header.body' })}
            </TierDescriptionText>
          </StyledContainer>
          <StyledImage src={CardImage} alt={'Mighty Oak Card'} />
        </HeaderRow>

        <Divider />

        <BenefitsSection
          tierProducts={selectedTierProducts}
          highestTierProducts={highestTierProducts}
          passionsEligible={isPassionsEligible}
        />

        {mocDDWaiverEnabled && (
          <PrivacyPolicy
            data-testid={TestIDs.PRICING_INTERSTITIAL.MOC_TIER_DIRECT_DEPOSIT_PRIVACY_POLICY}
          >
            *We’ll waive your subscription fee when you use{' '}
            <a
              onClick={handleOpenModal}
              onKeyDown={handleOpenModal}
              title="Direct Deposit Privacy Policy"
            >
              direct deposit{' '}
            </a>
            and deposit at least $250 in the past calendar month into your Acorns Checking account.{' '}
          </PrivacyPolicy>
        )}
        <AgreementModal isOpen={isShowingTerms} onCloseClick={handleCloseModal}>
          <AgreementDoc src={queryData?.acceptanceDocument.url} />
        </AgreementModal>

        <CTAContainer>
          <ActionWrapperDivider />
          <StyledButton
            data-testid={TestIDs.PRICING_INTERSTITIAL.MOC_NEXT_BUTTON}
            onPress={handleNext}
            disabled={isSubscribeToTierLoading || isTierGroupForUserLoading}
          >
            {isSubscribeToTierLoading ? (
              <Spinner height={20} width={20} white={true} />
            ) : (
              getRemoteString({ key: 'registration.moc_tier.cta.confirm' })
            )}
          </StyledButton>
        </CTAContainer>
      </MOCLayout>
    </>
  )
}

export default enhance(MOCTierPricing)
